import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import PageMeta from "@/components/feature/PageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ContentCard from "@/components/feature/ContentCard";
import ActorCard from "@/components/feature/ActorCard";
import EmptyState from "@/components/feature/EmptyState";
import Pagination from "@/components/feature/Pagination";
import { categoryMeta, type ContentItem } from "@/mocks/content";
import { countByType, getItemsByType, typeLabel } from "@/lib/content";

type CategoryName = keyof typeof categoryMeta;

const PAGE_SIZE = 4;
const ACTOR_PAGE_SIZE = 6;

function matchesFilter(item: ContentItem, filter: string): boolean {
  if (filter.startsWith("全部")) return true;
  if (item.type === "actor") return item.roleType === filter;
  return item.tags.includes(filter);
}

function sortItems(items: ContentItem[], sort: string): ContentItem[] {
  const sorted = [...items];
  if (sort === "按章节顺序") {
    return sorted.sort((a, b) => a.id.localeCompare(b.id));
  }
  if (sort === "按更新倒序" || sort === "按时间倒序") {
    return sorted.sort((a, b) => b.date.localeCompare(a.date));
  }
  if (sort === "按时间正序") {
    return sorted.sort((a, b) => a.date.localeCompare(b.date));
  }
  if (sort === "按篇幅排序") {
    return sorted.sort((a, b) => {
      const aViews = a.type === "drama" ? Number(a.views.replace(/[^\d.]/g, "")) : 0;
      const bViews = b.type === "drama" ? Number(b.views.replace(/[^\d.]/g, "")) : 0;
      return bViews - aViews;
    });
  }
  if (sort === "按姓氏排序") {
    return sorted.sort((a, b) => a.title.localeCompare(b.title, "zh-Hans-CN"));
  }
  return sorted;
}

export default function CategoryPage() {
  const { category } = useParams<{ category: string }>();
  const meta = category ? categoryMeta[category as CategoryName] : undefined;
  const [activeFilter, setActiveFilter] = useState(meta?.filters[0] ?? "全部");
  const [sort, setSort] = useState(meta?.sortOptions[0] ?? "");
  const [page, setPage] = useState(1);

  const resolvedSort = sort || meta?.sortOptions[0] || "";

  useEffect(() => {
    setActiveFilter(meta?.filters[0] ?? "全部");
    setSort(meta?.sortOptions[0] ?? "");
    setPage(1);
  }, [meta]);

  const allItems = useMemo(() => {
    if (!meta) return [];
    return getItemsByType(meta.type);
  }, [meta]);

  const filtered = useMemo(() => {
    const matched = allItems.filter((item) => matchesFilter(item, activeFilter));
    return sortItems(matched, resolvedSort);
  }, [allItems, activeFilter, resolvedSort]);

  if (!meta) {
    return (
      <PageShell>
        <PageMeta title="分类不存在 - 嫂子的诱惑" description="该分类暂不存在或已被移除，请返回首页浏览剧情介绍、演员资料与影视资讯。" />
        <div className="mx-auto w-full max-w-[1280px] px-4 py-24 md:px-6 lg:px-10">
          <EmptyState
            icon="ri-compass-3-line"
            title="分类暂无或已被移除"
            description="你访问的分类不存在，可以先回到首页继续浏览剧情、演员与资讯内容。"
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
            >
              返回首页
            </Link>
          </EmptyState>
        </div>
      </PageShell>
    );
  }

  const pageSize = meta.type === "actor" ? ACTOR_PAGE_SIZE : PAGE_SIZE;
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedItems = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const counts = countByType();

  const handleFilter = (filter: string) => {
    setActiveFilter(filter);
    setPage(1);
  };

  const handleReset = () => {
    setActiveFilter(meta.filters[0]);
    setSort(meta.sortOptions[0]);
    setPage(1);
  };

  const pageTitle = `${meta.title} | 嫂子的诱惑`;
  const pageDescription = `${meta.intro.slice(0, 72)} 共收录 ${allItems.length} 条${typeLabel[meta.type]}内容，支持筛选与分页浏览。`;

  return (
    <PageShell>
      <PageMeta
        title={pageTitle}
        description={pageDescription}
        keywords={`嫂子的诱惑,${meta.slug},${typeLabel[meta.type]},电视剧全集`}
      />

      <section className="border-b border-background-200/70 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 pb-10 pt-6 md:px-6 md:pb-12 lg:px-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: meta.slug }]} />
          <div className="mt-6 flex flex-wrap items-end justify-between gap-6">
            <div className="max-w-2xl">
              <p className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.24em] text-accent-700">
                <span className="h-px w-6 bg-accent-400" />
                {meta.kicker}
              </p>
              <h1 className="mt-3 font-heading text-3xl font-bold text-foreground-950 md:text-[40px]">
                {meta.title}
              </h1>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">{meta.intro}</p>
            </div>
            <div className="flex gap-3">
              <div className="rounded-lg border border-background-200 bg-background-50 px-5 py-3 text-center">
                <p className="font-heading text-2xl font-bold text-primary-600">{allItems.length}</p>
                <p className="mt-0.5 text-[11px] text-foreground-500">当前收录</p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 px-5 py-3 text-center">
                <p className="font-heading text-2xl font-bold text-accent-700">{filtered.length}</p>
                <p className="mt-0.5 text-[11px] text-foreground-500">筛选结果</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-12 lg:px-10">
        <div className="flex flex-col gap-4 rounded-lg border border-background-200/80 bg-background-50 p-4 md:flex-row md:items-center md:justify-between md:p-5">
          <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 no-scrollbar md:flex-wrap md:overflow-visible md:pb-0">
            {meta.filters.map((filter) => {
              const active = activeFilter === filter;
              return (
                <button
                  key={filter}
                  type="button"
                  onClick={() => handleFilter(filter)}
                  className={`shrink-0 whitespace-nowrap rounded-full border px-3.5 py-2 text-xs font-medium transition-colors ${
                    active
                      ? "border-primary-500 bg-primary-500 text-background-50"
                      : "border-background-300 text-foreground-600 hover:border-primary-300 hover:text-primary-700"
                  }`}
                >
                  {filter}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-2">
            <div className="relative">
              <select
                value={resolvedSort}
                onChange={(event) => {
                  setSort(event.target.value);
                  setPage(1);
                }}
                aria-label="排序方式"
                className="h-9 w-full appearance-none rounded-md border border-background-300 bg-background-50 pl-3 pr-8 text-xs text-foreground-700 outline-none transition-colors focus:border-primary-300 md:w-auto"
              >
                {meta.sortOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-base text-foreground-400" />
            </div>
            <button
              type="button"
              onClick={handleReset}
              className="inline-flex h-9 items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              <i className="ri-refresh-line text-sm" />
              重置
            </button>
          </div>
        </div>

        <p className="mt-6 flex items-center gap-2 text-xs text-foreground-500">
          <i className="ri-filter-3-line text-sm text-accent-700" />
          当前筛选：{activeFilter} · {resolvedSort} · 结果为站内演示数据
        </p>

        {pagedItems.length === 0 ? (
          <div className="mt-6">
            <EmptyState
              title={meta.empty}
              description="尝试切换筛选条件，或点击下方按钮查看全部内容。本站内容均为前端演示数据。"
            >
              <button
                type="button"
                onClick={handleReset}
                className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
              >
                <i className="ri-refresh-line text-base" />
                重置筛选条件
              </button>
            </EmptyState>
          </div>
        ) : (
          <div
            className={`mt-6 grid gap-4 ${
              meta.type === "actor"
                ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
                : "grid-cols-1 md:grid-cols-2"
            }`}
          >
            {pagedItems.map((item) =>
              item.type === "actor" ? (
                <ActorCard key={item.id} actor={item} />
              ) : (
                <ContentCard key={item.id} item={item} />
              ),
            )}
          </div>
        )}

        <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />

        <div className="mt-12 grid gap-4 rounded-lg border border-background-200/70 bg-background-100/70 p-5 sm:grid-cols-3">
          {[
            { label: "剧情介绍", key: "drama" as const, slug: "剧情介绍" },
            { label: "演员资料", key: "actor" as const, slug: "演员资料" },
            { label: "影视资讯", key: "news" as const, slug: "影视资讯" },
          ].map((entry) => (
            <Link
              key={entry.key}
              to={`/category/${encodeURIComponent(entry.slug)}`}
              className={`group flex items-center justify-between rounded-md border px-4 py-3 text-sm transition-colors ${
                meta.type === entry.key
                  ? "border-primary-200 bg-primary-50 text-primary-700"
                  : "border-background-200 bg-background-50 text-foreground-700 hover:border-primary-200 hover:text-primary-700"
              }`}
            >
              <span className="flex items-center gap-2">
                <i className="ri-folder-line text-base" />
                {entry.label}
                <span className="text-xs text-foreground-400">{counts[entry.key]} 条</span>
              </span>
              <i className="ri-arrow-right-s-line text-base transition-transform group-hover:translate-x-0.5" />
            </Link>
          ))}
        </div>
      </div>
    </PageShell>
  );
}